import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import TopNav from "@/components/TopNav";
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles, Send, X } from "lucide-react";

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick}
      className={`pb-3 px-1 text-sm font-semibold border-b-2 transition-colors ${active?"border-stone-900 text-stone-900":"border-transparent text-stone-500 hover:text-stone-800"}`}
      data-testid={testid}>
      {children}
    </button>
  );
}

function AIChat({ lessonId, onClose }) {
  const [msgs, setMsgs] = useState([{role:"ai", text:"Hi! I'm your TAWBTI tutor. Ask me anything about this lesson — definitions, examples, or how the five building blocks fit together."}]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(`s_${Date.now()}`);
  const send = async () => {
    if (!input.trim() || busy) return;
    const text = input.trim();
    setMsgs(m => [...m, {role:"user", text}]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/ai/tutor", { lesson_id: lessonId, session_id: sessionRef.current, message: text });
      setMsgs(m => [...m, {role:"ai", text: data.reply}]);
    } catch (e) {
      setMsgs(m => [...m, {role:"ai", text: "Sorry, the tutor is unavailable right now."}]);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed bottom-6 right-6 w-[380px] max-w-[92vw] h-[520px] tw-ai-glass rounded-2xl flex flex-col z-40" data-testid="ai-chat-panel">
      <div className="px-4 py-3 border-b border-white/40 flex items-center justify-between">
        <div className="flex items-center gap-2"><Sparkles size={16} className="text-amber-600"/> <span className="font-bold tw-display">AI Tutor</span></div>
        <button onClick={onClose} className="p-1 rounded-full hover:bg-white/60" data-testid="ai-chat-close"><X size={16}/></button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {msgs.map((m,i)=>(
          <div key={i} className={`text-sm leading-relaxed ${m.role==="user"?"text-stone-900":"text-stone-700"}`}>
            <div className="tw-mono-label mb-1 opacity-70">{m.role==="user"?"YOU":"TUTOR"}</div>
            <div className={`p-3 rounded-xl ${m.role==="user"?"bg-stone-900 text-white":"bg-white/90 border border-stone-200"}`}>{m.text}</div>
          </div>
        ))}
        {busy && <div className="text-xs text-stone-500" data-testid="ai-chat-thinking">Tutor is thinking…</div>}
      </div>
      <div className="p-3 border-t border-white/40 flex items-center gap-2">
        <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} placeholder="Ask a question…" className="tw-input flex-1" data-testid="ai-chat-input"/>
        <button onClick={send} className="tw-btn-primary p-3" disabled={busy} data-testid="ai-chat-send"><Send size={14}/></button>
      </div>
    </div>
  );
}

export default function LessonPlayer() {
  const { courseId, lessonId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("video");
  const [showAI, setShowAI] = useState(false);

  // Quiz state
  const [answers, setAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);

  // Course nav (next/prev lesson)
  const [courseData, setCourseData] = useState(null);

  useEffect(() => {
    setData(null); setQuizResult(null); setAnswers([]); setTab("video");
    (async () => {
      const { data } = await api.get(`/lessons/${lessonId}`);
      setData(data);
      setAnswers(new Array(data.quiz?.questions?.length || 0).fill(-1));
      const c = await api.get(`/courses/${courseId}`);
      setCourseData(c.data);
    })();
  }, [lessonId, courseId]);

  const lesson = data?.lesson;
  const block = data?.block;
  const quiz = data?.quiz;
  const allLessons = courseData ? courseData.blocks.flatMap(b => (b.lessons_data || []).map(l => ({...l, blockOrder: b.order, blockTitle: b.title}))) : [];
  const idx = allLessons.findIndex(l => l.lesson_id === lessonId);
  const prev = idx > 0 ? allLessons[idx-1] : null;
  const next = idx > -1 && idx < allLessons.length - 1 ? allLessons[idx+1] : null;

  const markComplete = async () => {
    await api.post("/progress", { course_id: courseId, lesson_id: lessonId, completed: true });
    if (next) nav(`/courses/${courseId}/lessons/${next.lesson_id}`);
    else nav(`/dashboard`);
  };

  const submitQuiz = async () => {
    const { data } = await api.post("/quiz/submit", { lesson_id: lessonId, answers });
    setQuizResult(data);
  };

  const tabs = lesson ? [
    { id: "video", label: "Video", hide: !lesson.video_url },
    { id: "slides", label: "Slides", hide: !lesson.slides_url },
    { id: "reading", label: "Reading", hide: !lesson.reading },
    { id: "interactive", label: "Interactive", hide: !lesson.interactive },
    { id: "quiz", label: "Quiz", hide: !quiz },
  ].filter(t=>!t.hide) : [];

  useEffect(() => {
    if (tabs.length && !tabs.find(t=>t.id===tab)) setTab(tabs[0].id);
  }, [tabs, tab]);

  if (!data || !courseData) return <div className="min-h-screen"><TopNav/><div className="p-10 text-stone-500" data-testid="lesson-loading">Loading lesson…</div></div>;

  return (
    <div className="min-h-screen">
      <TopNav/>
      <div className="max-w-5xl mx-auto px-6 py-8 fade-in" data-testid="lesson-container">
        <Link to="/dashboard" className="text-sm text-stone-500 inline-flex items-center gap-1 hover:text-stone-800" data-testid="lesson-back-dashboard"><ArrowLeft size={14}/> Back to dashboard</Link>
        <div className="mt-4 flex items-center gap-2 tw-mono-label" data-testid="lesson-breadcrumb">BLOCK {block.order} · {block.title}</div>
        <h1 className="tw-display text-3xl sm:text-4xl font-extrabold mt-2 tracking-tight" data-testid="lesson-title">{lesson.title}</h1>

        {lesson.objectives?.length > 0 && (
          <div className="tw-card p-5 mt-5">
            <div className="tw-mono-label">Learning objectives</div>
            <ul className="mt-3 space-y-1.5 list-disc list-inside text-stone-700">
              {lesson.objectives.map((o,i)=>(<li key={i}>{o}</li>))}
            </ul>
          </div>
        )}

        <div className="mt-8 border-b border-stone-200 flex gap-6">
          {tabs.map(t => <TabBtn key={t.id} active={tab===t.id} onClick={()=>setTab(t.id)} testid={`tab-${t.id}`}>{t.label}</TabBtn>)}
        </div>

        <div className="py-6 min-h-[280px]" data-testid={`tab-content-${tab}`}>
          {tab === "video" && lesson.video_url && (
            <div className="aspect-video rounded-2xl overflow-hidden bg-black">
              <iframe src={lesson.video_url} title="lesson video" className="w-full h-full" allowFullScreen/>
            </div>
          )}
          {tab === "slides" && lesson.slides_url && (
            <iframe src={lesson.slides_url} title="slides" className="w-full h-[480px] rounded-2xl border border-stone-200"/>
          )}
          {tab === "reading" && (
            <article className="prose prose-stone max-w-none whitespace-pre-wrap leading-relaxed text-stone-800" data-testid="reading-content">{lesson.reading}</article>
          )}
          {tab === "interactive" && (
            <div className="tw-card p-6 bg-amber-50/50 border-amber-200" data-testid="interactive-block">
              <div className="tw-mono-label" style={{color:"var(--tawbti-amber)"}}>Reflection</div>
              <p className="mt-2 text-stone-800 whitespace-pre-wrap leading-relaxed">{lesson.interactive}</p>
            </div>
          )}
          {tab === "quiz" && quiz && (
            <div data-testid="quiz-block">
              {!quizResult && (
                <div className="space-y-6">
                  {quiz.questions.map((q,i)=>(
                    <div key={i} className="tw-card p-5" data-testid={`quiz-question-${i}`}>
                      <div className="tw-mono-label">Question {i+1} of {quiz.questions.length}</div>
                      <div className="mt-2 font-semibold text-lg tw-display">{q.q}</div>
                      <div className="mt-4 space-y-2">
                        {q.options.map((opt, j)=>(
                          <label key={j} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition ${answers[i]===j?"border-stone-900 bg-stone-50":"border-stone-200 hover:bg-stone-50"}`} data-testid={`quiz-q${i}-opt${j}`}>
                            <input type="radio" name={`q-${i}`} checked={answers[i]===j} onChange={()=>{const a=[...answers]; a[i]=j; setAnswers(a);}}/>
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  <button onClick={submitQuiz} disabled={answers.some(a=>a===-1)} className="tw-btn-primary" data-testid="quiz-submit-btn">Submit quiz</button>
                </div>
              )}
              {quizResult && (
                <div className="tw-card p-6" data-testid="quiz-result">
                  <div className="flex items-center gap-3">
                    <div className="tw-display text-5xl font-extrabold" data-testid="quiz-score">{quizResult.score}%</div>
                    <div>
                      <div className={`tw-pill ${quizResult.passed?"tw-pill-done":"tw-pill-lock"}`}>{quizResult.passed?"Passed":"Try again"}</div>
                      <div className="text-stone-600 text-sm mt-1">{quizResult.correct} / {quizResult.total} correct</div>
                    </div>
                  </div>
                  <div className="mt-5 space-y-3">
                    {quizResult.results.map((r,i)=>(
                      <div key={i} className={`p-4 rounded-xl border ${r.is_correct?"border-green-200 bg-green-50":"border-red-200 bg-red-50"}`}>
                        <div className="font-medium text-stone-900">{r.question}</div>
                        <div className="text-sm mt-1 text-stone-700">Your answer: <strong>{r.options[r.user_answer] ?? "—"}</strong></div>
                        {!r.is_correct && <div className="text-sm mt-1 text-stone-700">Correct: <strong>{r.options[r.correct_answer]}</strong></div>}
                        {r.explanation && <div className="text-sm mt-2 text-stone-600 italic">{r.explanation}</div>}
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex gap-3">
                    <button onClick={()=>{setQuizResult(null); setAnswers(new Array(quiz.questions.length).fill(-1));}} className="tw-btn-outline" data-testid="quiz-retry-btn">Retake quiz</button>
                    {quizResult.passed && next && (
                      <button onClick={()=>nav(`/courses/${courseId}/lessons/${next.lesson_id}`)} className="tw-btn-primary inline-flex items-center gap-2" data-testid="quiz-next-btn">Next lesson <ArrowRight size={14}/></button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between border-t border-stone-200 pt-6">
          <button onClick={()=> prev && nav(`/courses/${courseId}/lessons/${prev.lesson_id}`)} className="tw-btn-outline inline-flex items-center gap-2 disabled:opacity-40" disabled={!prev} data-testid="lesson-prev-btn"><ArrowLeft size={14}/> Previous</button>
          <button onClick={markComplete} className="tw-btn-primary inline-flex items-center gap-2" data-testid="lesson-complete-btn"><CheckCircle2 size={16}/> Mark complete{next?" & continue":""}</button>
        </div>
      </div>

      <button onClick={()=>setShowAI(s=>!s)} className="fixed bottom-6 right-6 z-30 tw-btn-primary inline-flex items-center gap-2 shadow-xl" data-testid="ai-tutor-toggle">
        <Sparkles size={16}/> Ask AI tutor
      </button>
      {showAI && <AIChat lessonId={lessonId} onClose={()=>setShowAI(false)}/>}
    </div>
  );
}
